import { Component, DestroyRef, OnInit, computed, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router } from "@angular/router";
import { filter } from "rxjs/operators";

import { MonitoreoMatriz } from "src/app/shared/model/iso42001/matriz-riesgo.model";
import { Menu } from "src/app/shared/model/security/menu.model";
import { idEnSesion } from "src/app/vacaciones/frc-firmado/sesion";
import { MatrizRiesgoService } from "../matriz-riesgo.service";
import { ProximoMonitoreo, pideAlerta, proximoMonitoreo } from "../monitoreo";
import { AlertaMonitoreoService } from "./alerta-monitoreo.service";

/**
 * Alerta flotante de monitoreo (15/10/2026, pedido de Norman): desde 3 días
 * antes de que toque el monitoreo de un riesgo o de un activo, y mientras
 * siga vencido, sale en cualquier pantalla del COS una tarjeta flotante a
 * quien puede registrarlo (perfil de edición y menú de la matriz: lo decide
 * el servidor en matricesEditables). «Cerrar por hoy» la guarda hasta el día
 * siguiente; registrado el corte, ya no sale.
 *
 * La regla de fechas es la de siempre (monitoreo.ts); aquí solo se filtra y
 * se pinta. Dentro de una matriz no se repite lo de esa matriz: ahí cada
 * tarjeta ya dice «Vence en…».
 */

/** Un riesgo o un activo al que le toca monitoreo en 3 días o menos, o ya vencido. */
export interface PendienteMonitoreo {
  item: MonitoreoMatriz;
  proximo: ProximoMonitoreo;
}

/** Cada cuánto se vuelve a preguntar al navegar. */
export const REFRESCO_MS = 5 * 60 * 1000;

/** Cuántos caben en la tarjeta; los demás se ven en el tablero. */
export const MAXIMO_VISIBLES = 4;

/** El menú del módulo: sin él, el servidor no responde los monitoreos. */
const MENU_MODULO = "gestionRiesgos";

const CLAVE_CERRADA = "cos.alertaMonitoreo.cerradaEl.";

@Component({
  selector: "app-alerta-monitoreo",
  templateUrl: "./alerta-monitoreo.component.html",
  styleUrl: "./alerta-monitoreo.component.css",
})
export class AlertaMonitoreoComponent implements OnInit {
  private readonly service = inject(MatrizRiesgoService);
  private readonly router = inject(Router);
  private readonly alerta = inject(AlertaMonitoreoService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly todos = signal<PendienteMonitoreo[]>([]);
  private readonly matrizAbierta = signal<number | null>(null);
  private ultimaConsulta = 0;

  /** Si ya se cerró hoy. */
  readonly cerradaHoy = signal(false);

  /** Lo pendiente, menos lo de la matriz que se tiene abierta. */
  readonly pendientes = computed(() => {
    const abierta = this.matrizAbierta();
    return this.todos().filter((p) => p.item.idMatriz !== abierta);
  });
  readonly visibles = computed(() => this.pendientes().slice(0, MAXIMO_VISIBLES));
  readonly restantes = computed(() => Math.max(0, this.pendientes().length - MAXIMO_VISIBLES));
  readonly visible = computed(() => this.pendientes().length > 0 && !this.cerradaHoy());
  readonly hayVencidos = computed(() => this.pendientes().some((p) => p.proximo.estado === "VENCIDO"));
  readonly resumen = computed(() => resumenDe(this.pendientes()));

  ngOnInit(): void {
    this.matrizAbierta.set(matrizDeRuta(this.router.url));
    this.cargar();
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => {
        this.matrizAbierta.set(matrizDeRuta(e.urlAfterRedirects));
        if (Date.now() - this.ultimaConsulta >= REFRESCO_MS) {
          this.cargar();
        } else {
          // Si ya cambió el día, la que se cerró ayer vuelve a salir.
          this.cerradaHoy.set(this.leerCerrada() === hoy());
        }
      });
    this.alerta.refrescos.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.cargar());
  }

  cargar(): void {
    this.ultimaConsulta = Date.now();
    this.cerradaHoy.set(this.leerCerrada() === hoy());
    if (!tieneMenu(MENU_MODULO)) {
      this.todos.set([]);
      return;
    }
    this.service.consultarMonitoreos().subscribe({
      next: (res) => {
        // Sin permiso o con error, simplemente no hay alerta.
        const valida = !!res && !res.codigoError;
        this.todos.set(valida ? pendientesDe(res.monitoreos || [], res.matricesEditables || []) : []);
      },
      error: () => this.todos.set([]),
    });
  }

  abrir(p: PendienteMonitoreo): void {
    this.router.navigate(["/RIESGOS/matriz", p.item.idMatriz]);
  }

  verTodos(): void {
    this.router.navigate(["/RIESGOS/gestionRiesgos"]);
  }

  /** Hasta mañana: cada día vuelve a salir mientras siga pendiente. */
  cerrar(): void {
    try {
      localStorage.setItem(this.clave(), hoy());
    } catch {
      // Sin almacenamiento del navegador se cierra solo mientras dure esta vista.
    }
    this.cerradaHoy.set(true);
  }

  private leerCerrada(): string | null {
    try {
      return localStorage.getItem(this.clave());
    } catch {
      return null;
    }
  }

  private clave(): string {
    return CLAVE_CERRADA + (idEnSesion() ?? "sin-sesion");
  }
}

/**
 * De las matrices donde puede registrar el monitoreo, lo que vence en 3 días
 * o menos y lo vencido; lo más atrasado primero.
 */
export function pendientesDe(monitoreos: MonitoreoMatriz[], editables: number[]): PendienteMonitoreo[] {
  const puede = new Set(editables);
  const lista: PendienteMonitoreo[] = [];
  for (const item of monitoreos) {
    if (!puede.has(item.idMatriz)) {
      continue;
    }
    const proximo = proximoMonitoreo(item.periodicidad, item.ultimoCorte, item.fechaIdentificacion,
      item.tipo === "ACTIVO");
    if (pideAlerta(proximo)) {
      lista.push({ item, proximo });
    }
  }
  return lista.sort((a, b) => (a.proximo.dias ?? 0) - (b.proximo.dias ?? 0));
}

/** «2 vencidos y 1 por vencer», para el renglón bajo el título. */
export function resumenDe(pendientes: PendienteMonitoreo[]): string {
  const vencidos = pendientes.filter((p) => p.proximo.estado === "VENCIDO").length;
  const porVencer = pendientes.length - vencidos;
  const partes: string[] = [];
  if (vencidos) {
    partes.push(vencidos + (vencidos === 1 ? " vencido" : " vencidos"));
  }
  if (porVencer) {
    partes.push(porVencer + " por vencer");
  }
  return partes.join(" y ") + (pendientes.length === 1 ? " en una matriz que puedes editar." : " en matrices que puedes editar.");
}

/** El id de la matriz abierta en /RIESGOS/matriz/<id>; null fuera de una matriz. */
function matrizDeRuta(url: string | null | undefined): number | null {
  const m = /^\/RIESGOS\/matriz\/(\d+)/.exec(url || "");
  return m ? Number(m[1]) : null;
}

/** Si el menú que dejó el login trae ese factory, en cualquier nivel. */
function tieneMenu(factory: string): boolean {
  try {
    const raw = localStorage.getItem("menuItems");
    const menus: Menu[] = raw ? JSON.parse(raw) : [];
    const busca = (lista: Menu[] | null | undefined): boolean =>
      (lista || []).some((m) => m?.factory === factory || busca(m?.hijos));
    return busca(menus);
  } catch {
    return false;
  }
}

/** Hoy, en hora local: AAAA-MM-DD. */
function hoy(): string {
  const d = new Date();
  return d.getFullYear() + "-" + ("0" + (d.getMonth() + 1)).slice(-2) + "-" + ("0" + d.getDate()).slice(-2);
}
